import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Disposition } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { StyledDiv, md, phoneOnly } from '../../../../common/ConstantsStyles';
import { CeOffersData } from '../../../../common/PSXHeader/SearchBar/utils';
import { slideContainerStyle } from '../../../../common/SnackCard/SnackCard.styles';
import { getFeatureFlag } from '../../../../common/Utils';
import { makeAllSlidesAriaHidden } from '../../../../common/Utils/a11yUtils/a11yUtils';
import { useSaveOfferDisposition } from '../../../../hooks/useCeOffers';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSnackcardViewAllCount } from '../../../../hooks/useSnackcardViewAllCount';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import {
  getCurrentMember,
  getEnterpriseId,
} from '../../../../utils/user.utils';
import {
  programPromotionCardStyle,
  programPromotionCarouselStyle,
  programPromotionDescriptionStyle,
  programPromotionSlideStyle,
  programPromotionTitleStyle,
} from './ProgramPromotionCard.styled';
import { ProgramPromotionModal } from './ProgramPromotionModal';

interface ProgramPromotionCardProps {
  offers: any;
  searchTerm?: string;
}

export const ProgramPromotionCard: React.FC<ProgramPromotionCardProps> = ({
  offers,
  searchTerm = '',
}) => {
  const isPreEffective = useCurrentMemberIsPreEffective();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOfferName, setSelectedOfferName] = useState<string | null>(
    null
  );
  const [slidesPerView, setSlidesPerView] = useState(3);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const [maxCardHeight, setMaxCardHeight] = useState<number>();
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [, saveOfferDisposition] = useSaveOfferDisposition({});
  const currentMember = getCurrentMember();
  const allConfigs = useLagoon('config');
  const data = useLagoon('ui-messaging')();

  const phoneScreen = useMediaQuery(phoneOnly);
  const isSingleSlideView = winWidth < md;

  const { isExpandViewAllEnabled } = useSnackcardViewAllCount(offers?.length);

  const enableProgramPromotionModalPopup = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_MODAL_POPUP'
  );

  const programPromotionContent = find(data, {
    key: ConstantsLagoon.PROGRAM_PROMOTION.PROGRAM_PROMOTION_CONTENT,
  });

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enableAnalytics: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION_ANALYTICS
  );

  const offerTokensKey =
    offers
      ?.map((offer) => offer?.offerToken)
      .filter(Boolean)
      .join(',') || '';

  useEffect(() => {
    // Track keyword search that resulted in offer being shown
    if (enableAnalytics && searchTerm && offers.length > 0) {
      offers.forEach((offer) => {
        adobeLinkTrackEvent({
          name: `${offer?.offerName} | offer shown`,
          location: programPromotionContent?.title,
          userSelections: `keyword:${searchTerm}|offer:${offer?.offerName}`,
        });
      });
    }
  }, [offerTokensKey, enableAnalytics]);

  useEffect(() => {
    let windowWidth = window.innerWidth;
    function handleResize() {
      windowWidth = window.innerWidth;
      setWinWidth(windowWidth);
    }
    handleResize();
    window.addEventListener('resize', handleResize);

    if (winWidth > 872) {
      if (winWidth > 872 && offers?.length >= 3) setSlidesPerView(2);
      else if (winWidth < 1250 && offers?.length === 2) setSlidesPerView(3);
      else if (winWidth < 1250 && offers?.length > 2) setSlidesPerView(2);
      else if (winWidth > 1250 && offers?.length >= 2) setSlidesPerView(3);
      else if (offers?.length >= 1) setSlidesPerView(3);
    }
    if (winWidth < 872) setSlidesPerView(1);
    if (
      isExpandViewAllEnabled &&
      winWidth > 868 &&
      (offers?.length === 1 || offers?.length === 2)
    )
      setSlidesPerView(2);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [winWidth, isExpandViewAllEnabled, offers?.length]);

  useLayoutEffect(() => {
    setMaxCardHeight(undefined);

    const frameId = window.requestAnimationFrame(() => {
      const tallestCard = cardRefs.current.reduce((maxHeight, card) => {
        if (!card) {
          return maxHeight;
        }

        return Math.max(maxHeight, card.offsetHeight);
      }, 0);

      setMaxCardHeight(tallestCard || undefined);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [offers, phoneScreen, slidesPerView, winWidth]);

  const handleOfferClick = (e: React.MouseEvent, offer: any) => {
    e.preventDefault();
    if (isPreEffective || !offer?.cta?.ctaValue) return;

    const enterpriseId = getEnterpriseId(currentMember);

    // Track offer click
    if (enableAnalytics) {
      adobeLinkTrackEvent({
        name: offer.offerName,
        location: programPromotionContent?.title,
        destinationUrl: '',
        userSelections: searchTerm
          ? `keyword:${searchTerm}|offer:${offer?.offerName}`
          : '',
      });
    }

    if (enableProgramPromotionModalPopup === 'Y') {
      setSelectedOfferName(offer.offerName);
      setIsModalOpen(true);
    } else {
      if (offer?.offerToken && enterpriseId) {
        saveOfferDisposition({
          variables: {
            enterpriseId,
            disposition: Disposition.ACCEPTED,
            offerToken: offer.offerToken,
          },
        });
      }

      // Open vendor website directly
      window.open(offer.cta.ctaValue, '_blank');
    }
  };

  const isOfferLinkApplicable = (offer: CeOffersData) =>
    !isPreEffective && Boolean(offer?.cta?.ctaValue);

  const renderOfferCard = (offer: any, index: number, isInCarousel = false) => (
    <div
      key={offer?.offerId || index}
      ref={(element) => {
        cardRefs.current[index] = element;
      }}
      style={{ height: '100%' }}
    >
      <Card
        css={programPromotionCardStyle(
          isInCarousel,
          isPreEffective,
          offer?.cta?.ctaValue,
          maxCardHeight
        )}
        data-testid={`program-promotion-card-${offer?.offerName}`}
      >
        <StyledDiv
          css={{
            padding: '0',
            display: 'flex',
            gap: '8px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <Flex
            alignItems="flex-start"
            css={{
              gap: '16px',
              width: '100%',
            }}
            direction="column"
          >
            {offer?.logo && (
              <img
                alt={`${offer?.offerName} logo`}
                data-testid={`program-promotion-${offer?.offerName}-logo`}
                src={offer.logo}
                style={{
                  height: 'auto',
                  maxHeight: '30px',
                  width: 'auto',
                  maxWidth: '150px',
                  backgroundImage: 'none',
                }}
              />
            )}
            <Text
              color="#002677"
              css={programPromotionTitleStyle}
              data-testid={`program-promotion-${offer?.offerName}-name`}
              fontSize="lg"
              fontWeight="medium"
            >
              {offer?.offerName}
            </Text>
          </Flex>

          <Flex
            css={{
              gap: '16px',
              height: '100%',
            }}
            direction="column"
            justify="space-between"
          >
            <Text
              color="$core.color.neutral.80"
              css={programPromotionDescriptionStyle}
              data-testid={`program-promotion-${offer?.offerName}-description`}
              fontSize="md"
              fontWeight="regular"
            >
              {offer?.offerDescription}
            </Text>
            {isOfferLinkApplicable(offer) && (
              <Link
                css={{
                  lineHeight: '20px',
                  textAlign: 'left',
                  width: 'fit-content',
                }}
                data-auto-testid={`program-promotion-${offer?.offerName}-link-text`}
                data-testid={`program-promotion-${offer?.offerName}-link-text`}
                isStandardAnchor
                onClick={(e) => handleOfferClick(e, offer)}
                size="$md"
              >
                {offer?.cta?.ctaText || 'Learn More'}
                <IconSymbol
                  icon="launch"
                  size="20"
                  style={{ marginLeft: '4px', visibility: 'inherit' }}
                />
              </Link>
            )}
          </Flex>
        </StyledDiv>
      </Card>
    </div>
  );

  const offerCards = offers?.map((offer, index) =>
    renderOfferCard(offer, index, false)
  );

  const offerCardsForCarousel = offers?.map((offer, index) =>
    renderOfferCard(offer, index, true)
  );

  const shouldShowCarousel = (offers?.length || 0) > slidesPerView;

  return (
    <>
      <Layout.Stack
        css={{
          marginBottom: '8px',
          width: '100%',
        }}
        grow
        space={0}
      >
        {Array.isArray(offers) && offers?.length > 0 ? (
          shouldShowCarousel ? (
            <Carousel
              css={programPromotionCarouselStyle(
                offers?.length,
                winWidth,
                isSingleSlideView,
                false,
                isExpandViewAllEnabled
              )}
              label=""
              nextSlideOnClick={() => makeAllSlidesAriaHidden()}
              prevSlideOnClick={() => makeAllSlidesAriaHidden()}
              slideIndexOnClick={() => makeAllSlidesAriaHidden()}
              slides={offerCardsForCarousel.map((slide, id) => (
                <Slide
                  css={programPromotionSlideStyle(
                    id,
                    offers?.length,
                    isExpandViewAllEnabled
                  )}
                  key={id}
                >
                  <div css={slideContainerStyle}>{slide}</div>
                </Slide>
              ))}
              slidesPerView={slidesPerView}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '16px',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
                width: '100%',
                marginTop: '2px',
              }}
            >
              {offerCards.map((slide) => slide)}
            </div>
          )
        ) : null}
      </Layout.Stack>

      <ProgramPromotionModal
        isOpen={isModalOpen}
        keywordSearchTerm={searchTerm}
        onClose={() => setIsModalOpen(false)}
        programPromotions={offers}
        selectedOfferName={selectedOfferName}
      />
    </>
  );
};
